// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The development team's notes on a report: the comments on its ClickUp ticket
// that a developer started with [client-facing] (owner's decisions 2026-09-14,
// docs/plans/DECISIONS.md D37, D38) — the server keeps every other one back and
// takes the marker off. Read-only here: replying to them happens in ClickUp.
//
// Read live: fetched when a report is opened, kept in the page's cache for the
// rest of the visit so clicking between reports does not ask ClickUp again, and
// fetched afresh after the page's Refresh. Lifted out of the old Comments section
// when D47 folded it into the one Conversation, so the tab can count them.

import { useEffect, useState } from 'react';
import { getBugReportComments, type BugReportComment } from '../../lib/bugReportApi';

export type CommentsResult = { comments: BugReportComment[]; truncated: boolean };

export type TeamNotes = {
  /** Null until the first answer. */
  result: CommentsResult | null;
  /** A failed read — never shown as "no notes": only an answer means nobody has replied. */
  error: string | null;
  retry: () => void;
};

export function useTeamNotes(reportId: string, cache: Map<string, CommentsResult>, refreshKey: number): TeamNotes {
  const [result, setResult] = useState<CommentsResult | null>(() => cache.get(reportId) ?? null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setError(null);
    const cached = cache.get(reportId);
    if (cached) {
      setResult(cached);
      return undefined;
    }
    setResult(null);
    let cancelled = false;
    getBugReportComments(reportId)
      .then((res) => {
        if (cancelled) return;
        cache.set(reportId, res.data);
        setResult(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "The development team's notes could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, cache, refreshKey, attempt]);

  return { result, error, retry: () => setAttempt((n) => n + 1) };
}

/** How many notes and replies there are, for the tab's count. */
export function teamNoteCount(result: CommentsResult | null): number {
  return result ? result.comments.reduce((n, c) => n + 1 + c.replies.length, 0) : 0;
}
